import {
  Badge,
  Box,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  InputGroup,
  InputText,
  Stack,
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableHead,
  TableHeadCell,
  TableHeadRow,
} from "@aksara-ui/react";
import TableBodyRowsSkeleton from "components/TableBodyRowSkeleton";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllTenantsFilter,
  setLoadingModal,
  storeTierRewards,
} from "redux/actions";
import moment from "moment";
import { InputCheckbox } from "components/Form/InputCheckbox";
import { showMessage } from "utilities/helper";
import CatalogService from "service/Catalog";
import VoucherService from "service/Voucher";
import { Field, FieldArray, FieldProps, Form, Formik } from "formik";
import { InputSelect } from "components/Form/InputSelect";
import FilterKit from "components/FilterKit";
import { getRole } from "hooks";
import { LazyLoadImage } from "react-lazy-load-image-component";

interface Props {
  tierId?: string | undefined;
  tierName?: string | undefined;
  type?: string | undefined;
}

const initialValues = {
  tierRewardsInput: [
    {
      quantity: "",
      category: "",
    },
  ],
};

interface ValueSchema {
  label: string;
  value: string | number;
}

const TierRewardAddModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  tierId,
  tierName,
  type,
}) => {
  const dispatch = useDispatch();
  const userRole = getRole();

  const [selectedRows, setSelectedRows] = useState<object[]>([]);
  const [datas, setDatas] = useState<any>([]);

  const { appUrl, isLoadingModal } = useSelector(
    (state: any) => state.globalReducer
  );

  const [category, setCategory] = useState<ValueSchema | null>(null);

  const { isFormLoading } = useSelector((state: any) => state.tierReducer);
  const { tenantsFilter } = useSelector((state: any) => state.tenantReducer);

  const [categoryItems] = React.useState([
    { label: "Checkpoint", value: "checkpoint" },
    { label: "Birthday", value: "birthday" },
    { label: "Monthly", value: "monthly" },
  ]);

  const handleCloseModal = () => {
    onClose && onClose();
    setSelectedRows([]);
    setDatas([]);
  };

  const handleSubmit = () => {
    const catalogs = selectedRows;

    dispatch(
      storeTierRewards(
        tierId,
        { catalogs },
        tierName,
        type,
        handleCloseModal
      ) as any
    );
  };

  const getAllDatas = async (
    page: number,
    type?: string,
    tenantsFilter: number = 3
  ) => {
    dispatch(setLoadingModal(true));
    try {
      let data = [];
      if (type === "Catalog") {
        data = await CatalogService.getAllCatalogs(page, "", 50, tenantsFilter);
      } else if (type === "Voucher") {
        data = await VoucherService.getAllVouchers(page, "", 50, tenantsFilter);
      }

      dispatch(setLoadingModal(false));
      setDatas(data.catalogs);
    } catch (err: any) {
      dispatch(setLoadingModal(false));
      if (err.request.status !== 401) {
        console.log("error :", err);
        showMessage(
          !err.response
            ? err.message
            : !!err.response?.data?.error
            ? err.response?.data?.error
            : err.response?.data?.message,
          "error"
        );
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      dispatch(getAllTenantsFilter() as any);
      getAllDatas(1, type, 3);
    }
  }, [dispatch, isOpen]);

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
      maxWidth={"1000px"}
    >
      <Formik initialValues={initialValues} onSubmit={handleSubmit}>
        {({ values, setFieldValue }) => {
          return (
            <Form>
              <DialogHeader>Add Tier Reward</DialogHeader>
              <DialogContent>
                {userRole !== "tenant" ? (
                  <>
                    <FilterKit
                      orderByOptions={[
                        {
                          key: tenantsFilter,
                          label: "Tenant",
                          options: tenantsFilter.map((tenant: any) => {
                            return {
                              label: tenant.name,
                              value: tenant.id,
                            };
                          }),
                        },
                      ]}
                      onChangeOrder={(e: any) => {
                        getAllDatas(
                          1,
                          type,
                          e[tenantsFilter] ? e[tenantsFilter].value : ""
                        );
                      }}
                    />
                    <Box marginBottom={10} />
                  </>
                ) : (
                  <></>
                )}
                <Card maxHeight={"100%"} overflow={"auto"} elevation={2}>
                  <FieldArray name="tierRewardsInput">
                    {() => {
                      return (
                        values.tierRewardsInput.length > 0 &&
                        values.tierRewardsInput.map(
                          (tierRewardInput, index) => {
                            return (
                              <Table key={index}>
                                <TableHead
                                  position={"sticky"}
                                  top={0}
                                  backgroundColor={"white"}
                                >
                                  <TableHeadRow>
                                    <TableHeadCell>
                                      <InputCheckbox
                                        checked={
                                          selectedRows.length > 0 &&
                                          selectedRows.length < datas.length
                                            ? "indeterminate"
                                            : !!selectedRows.length
                                        }
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            setSelectedRows(
                                              datas.map(({ id }: any) => ({
                                                id: id,
                                                quantity:
                                                  values.tierRewardsInput[index]
                                                    .quantity,
                                                category:
                                                  values.tierRewardsInput[index]
                                                    .category,
                                              }))
                                            );
                                          } else {
                                            setSelectedRows([]);
                                          }
                                        }}
                                      />
                                    </TableHeadCell>
                                    <TableHeadCell>{type} Name</TableHeadCell>
                                    <TableHeadCell>Image</TableHeadCell>
                                    <TableHeadCell>Product</TableHeadCell>
                                    <TableHeadCell>Expiry Date</TableHeadCell>
                                    <TableHeadCell>Quantity</TableHeadCell>
                                    <TableHeadCell minWidth={150}>
                                      Category
                                    </TableHeadCell>
                                  </TableHeadRow>
                                </TableHead>
                                <TableBody>
                                  {isLoadingModal ? (
                                    <TableBodyRowsSkeleton
                                      rowsNumber={10}
                                      columnsNumber={7}
                                    />
                                  ) : !datas.length ? (
                                    <TableBodyRow>
                                      <TableBodyCell
                                        colSpan={7}
                                        textAlign={"center"}
                                      >
                                        No reward available
                                      </TableBodyCell>
                                    </TableBodyRow>
                                  ) : (
                                    datas.map((data: any) => {
                                      return (
                                        <TableBodyRow key={data.id}>
                                          <TableBodyCell>
                                            <InputCheckbox
                                              checked={
                                                selectedRows.findIndex(
                                                  (selectedRow:any) => 
                                                    selectedRow["id"] ===
                                                    data.id
                                                ) !== -1
                                              }
                                              onCheckedChange={(checked) => {
                                                if (checked) {
                                                  setSelectedRows((prev) => [
                                                    ...prev,
                                                    {
                                                      id: data.id,
                                                      quantity:
                                                        values.tierRewardsInput[
                                                          index
                                                        ].quantity,
                                                      category:
                                                        values.tierRewardsInput[
                                                          index
                                                        ].category,
                                                    },
                                                  ]);
                                                } else {
                                                  setSelectedRows((prev) =>
                                                    prev.filter(
                                                      (item:any) =>
                                                        item["id"] !== data.id
                                                    )
                                                  );
                                                }
                                              }}
                                            />
                                          </TableBodyCell>
                                          <TableBodyCell>
                                            {data.catalog_name}
                                          </TableBodyCell>
                                          <TableBodyCell>
                                            {data.img_url ? (
                                              <LazyLoadImage
                                                key={data.id}
                                                src={`${appUrl}/storage/${data.img_url}`}
                                                alt="catalog-image"
                                                height={"100%"}
                                                width={120}
                                                style={{ borderRadius: 20 }}
                                                effect="blur"
                                              />
                                            ) : (
                                              ""
                                            )}
                                          </TableBodyCell>
                                          <TableBodyCell>
                                            {data.catalog_product}
                                          </TableBodyCell>
                                          <TableBodyCell whiteSpace={"nowrap"}>
                                            <Badge size="md" variant="info">
                                              {moment(data.expiry_date).format(
                                                "DD MMM YYYY"
                                              )}
                                            </Badge>
                                          </TableBodyCell>
                                          <TableBodyCell>
                                            <Field
                                              name={`tierRewardsInput.${index}.quantity`}
                                            >
                                              {({
                                                field,
                                                meta,
                                              }: FieldProps<string>) => (
                                                <Stack spacing="xs">
                                                  <InputGroup inputSize={40}>
                                                    <InputText
                                                      type="text"
                                                      size={40}
                                                      width={60}
                                                      onChange={(e: any) => {
                                                        setFieldValue(
                                                          `tierRewardsInput.${index}.quantity`,
                                                          parseInt(
                                                            e.target.value
                                                          )
                                                        );

                                                        const checked =
                                                          selectedRows.findIndex(
                                                            (selectedRow:any) =>
                                                              selectedRow[
                                                                "id"
                                                              ] === data.id
                                                          ) !== -1
                                                            ? true
                                                            : false;

                                                        if (checked) {
                                                          setSelectedRows(
                                                            selectedRows.map(
                                                              (
                                                                selectedRow: any
                                                              ) =>
                                                                selectedRow.id ===
                                                                data.id
                                                                  ? {
                                                                      id: data.id,
                                                                      quantity:
                                                                        parseInt(
                                                                          e
                                                                            .target
                                                                            .value
                                                                        ),
                                                                      category:
                                                                        selectedRow.category,
                                                                    }
                                                                  : selectedRow
                                                            )
                                                          );
                                                        }
                                                      }}
                                                    />
                                                  </InputGroup>
                                                </Stack>
                                              )}
                                            </Field>
                                          </TableBodyCell>
                                          <TableBodyCell>
                                            <Field
                                              name={`tierRewardsInput.${index}.category`}
                                            >
                                              {({
                                                field,
                                                meta,
                                              }: FieldProps<string>) => (
                                                <Stack spacing="xs">
                                                  <InputGroup inputSize={40}>
                                                    <InputSelect
                                                      selectedItem={category}
                                                      width={"100%"}
                                                      placeholder="Select Category"
                                                      handleSelectedItemChange={({
                                                        selectedItem,
                                                      }) => {
                                                        if (selectedItem) {
                                                          setCategory(
                                                            selectedItem
                                                          );
                                                          setFieldValue(
                                                            `tierRewardsInput.${index}.category`,
                                                            selectedItem.value
                                                          );

                                                          const checked =
                                                            selectedRows.findIndex(
                                                              (selectedRow:any) =>
                                                                selectedRow[ "id"] === data.id
                                                            ) !== -1
                                                              ? true
                                                              : false;

                                                          if (checked) {
                                                            setSelectedRows(
                                                              selectedRows.map(
                                                                (
                                                                  selectedRow: any
                                                                ) =>
                                                                  selectedRow.id ===
                                                                  data.id
                                                                    ? {
                                                                        id: data.id,
                                                                        quantity:
                                                                          selectedRow.quantity,
                                                                        category:
                                                                          selectedItem.value,
                                                                      }
                                                                    : selectedRow
                                                              )
                                                            );
                                                          }
                                                        }
                                                      }}
                                                      itemToString={(item) =>
                                                        item
                                                          ? `${item.label}`
                                                          : ""
                                                      }
                                                      itemValue={(item) =>
                                                        item
                                                          ? `${item.value}`
                                                          : ""
                                                      }
                                                      items={categoryItems}
                                                      size={"lg"}
                                                      borderRadius={"6px"}
                                                      errors={
                                                        !!meta.error &&
                                                        meta.touched
                                                      }
                                                    />
                                                  </InputGroup>
                                                </Stack>
                                              )}
                                            </Field>
                                          </TableBodyCell>
                                        </TableBodyRow>
                                      );
                                    })
                                  )}
                                </TableBody>
                              </Table>
                            );
                          }
                        )
                      );
                    }}
                  </FieldArray>
                </Card>
              </DialogContent>
              <DialogFooter>
                <Stack
                  display={"flex"}
                  flexDirection={"row"}
                  justifyContent={"right"}
                  spacing={"xs"}
                  flex={1}
                  style={{ gap: 15 }}
                  paddingY={0}
                >
                  <Button
                    variant={"outline"}
                    onClick={() => handleCloseModal()}
                  >
                    Cancel
                  </Button>
                  <Button
                    type={"submit"}
                    isLoading={isFormLoading}
                    variant={"primary"}
                  >
                    Add Tier Reward
                  </Button>
                </Stack>
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default TierRewardAddModal;
